import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import AWNLogo from '../components/AWNLogo';
import useAuthEmailCooldown from '../hooks/useAuthEmailCooldown';
import { normalizeEmail, sendPasswordResetEmail } from '../services/authService';
import { isValidEmail } from '../utils/authValidation';
import styles from '../style/ForgotPasswordPage.module.css';

function ForgotPasswordPage() {
  const location = useLocation();
  const [email, setEmail] = useState(() => (
    typeof location.state?.email === 'string' ? location.state.email : ''
  ));
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const { secondsRemaining, startCooldown } = useAuthEmailCooldown();
  const normalizedEmail = normalizeEmail(email);

  const requestReset = async () => {
    setIsLoading(true);
    setStatus(null);
    try {
      await sendPasswordResetEmail(normalizedEmail);
      setEmail(normalizedEmail);
      setIsSubmitted(true);
      setStatus({
        type: 'success',
        message: 'Wenn für diese E-Mail ein Konto besteht, wurde ein sicherer Reset-Link angefordert.',
      });
      startCooldown();
    } catch (error) {
      setStatus({ type: 'error', message: error.message });
      if (error?.code?.includes('rate_limit')) startCooldown();
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isValidEmail(email)) {
      setStatus({ type: 'error', message: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.' });
      return;
    }
    await requestReset();
  };

  return (
    <div className={styles.pageContainer}>
      <div className={styles.backgroundPattern} aria-hidden="true" />
      <div className={styles.splitContainer}>
        <aside className={styles.socialColumn}>
          <div className={styles.socialLogoSection}>
            <AWNLogo width={300} height={100} className={styles.companyLogo} />
          </div>
          <p className={styles.sideCopy}>Sie erhalten einen einmal verwendbaren Link. Ihr bisheriges Passwort bleibt gültig, bis Sie ein neues festlegen.</p>
        </aside>

        <section className={styles.forgotCard}>
          {!isSubmitted ? (
            <>
              <div className={styles.logoSection}>
                <p className={styles.eyebrow}>Kontozugang wiederherstellen</p>
                <h1 className={styles.forgotTitle}>Passwort vergessen?</h1>
                <p className={styles.forgotSubtitle}>Geben Sie die E-Mail-Adresse Ihres AWN-Kontos ein.</p>
              </div>

              <form onSubmit={handleSubmit} className={styles.forgotForm} noValidate>
                <div className={styles.inputGroup}>
                  <label htmlFor="email">E-Mail-Adresse</label>
                  <div className={styles.inputWrapper}>
                    <input type="email" id="email" value={email} onChange={(event) => { setEmail(event.target.value); setStatus(null); }} placeholder="ihre.email@beispiel.de" autoComplete="username" inputMode="email" disabled={isLoading} />
                    <span className={styles.inputIcon} aria-hidden="true">@</span>
                  </div>
                </div>

                <button type="submit" className={styles.submitButton} disabled={isLoading}>
                  {isLoading ? 'Reset-Link wird angefordert…' : 'Reset-Link anfordern'}
                </button>

                {status && <div className={styles.errorText} role="alert">{status.message}</div>}

                <div className={styles.backSection}>
                  <Link to="/login" state={{ email: normalizedEmail }} className={styles.rememberLink}>Zurück zur Anmeldung</Link>
                </div>
              </form>
            </>
          ) : (
            <div className={styles.outcomePanel} aria-live="polite">
              <div className={styles.outcomeIcon} aria-hidden="true">✉</div>
              <p className={styles.eyebrow}>E-Mail angefordert</p>
              <h1 className={styles.forgotTitle}>Prüfen Sie Ihr Postfach</h1>
              <p className={styles.forgotSubtitle}>Wenn ein Konto für <strong>{normalizedEmail}</strong> besteht, erhalten Sie dort den Reset-Link.</p>
              <ol className={styles.checklist}>
                <li>Posteingang sowie Spam- und Werbung-Ordner prüfen.</li>
                <li>Nur den neuesten Reset-Link verwenden.</li>
                <li>Im geöffneten Fenster ein neues Passwort festlegen.</li>
              </ol>

              {status && (
                <div className={status.type === 'error' ? styles.errorText : styles.successText} role="status">{status.message}</div>
              )}

              <div className={styles.actionButtons}>
                <Link to="/login" state={{ email: normalizedEmail }} className={styles.backToLogin}>Zur Anmeldung</Link>
                <button type="button" className={styles.resendButton} onClick={requestReset} disabled={isLoading || secondsRemaining > 0}>
                  {isLoading ? 'Wird angefordert…' : secondsRemaining > 0 ? `Erneut in ${secondsRemaining}s` : 'Link erneut anfordern'}
                </button>
              </div>
              <button type="button" className={styles.textButton} onClick={() => { setIsSubmitted(false); setStatus(null); }}>Andere E-Mail verwenden</button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export default ForgotPasswordPage;
